import './kp-file-editor.less';

import type {ViewableFile} from 'typings/portaro.be.types';
import type {State} from '../../constants/portaro.constants';
import {ngAsync} from 'shared/utils/ng-@decorators';
import type {IOnChanges, ISCEService} from 'angular';
import type {Component} from 'typings/portaro.fe.types';
import type {Ace} from 'ace-builds';
import {FileViewForm} from 'typings/file-view-form';
import type {FileService} from '../../../file/file.service';

enum EditorMode {
    VELOCITY = 'velocity',
    HTML = 'html',
    PROPERTIES = 'properties',
    CSS = 'css'
}

interface ImageFile extends ViewableFile {
    width: number;
    height: number;
    urlPath: string;
}

const DEFAULT_EDITOR_MODE: EditorMode = EditorMode.VELOCITY;

export abstract class KpFileEditorComponentController implements KpFileEditorComponentBindings, IOnChanges {
    public file: ViewableFile;

    public abstract onEditorInit(locals: { $editor: Ace.Editor });

    public state: State = 'INITIAL';
    public contentHolder: { value: any } = {value: null};
    private savedContent: any;
    private aceOptions;

    /*@ngInject*/
    constructor(private fileService: FileService, private CONTEXT_PATH: string, private SERVER_URL: string, private $sce: ISCEService) {
    }

    public $onChanges(onChangesObj: angular.IOnChangesObject): void {

        if (onChangesObj.file && onChangesObj.file.currentValue) {
            const currentFile: ViewableFile = onChangesObj.file.currentValue;

            if (currentFile.viewForms.includes(FileViewForm.PLAIN_TEXT)) {
                this.aceOptions = {
                    useWrapMode: false,
                    showGutter: true,
                    mode: this.getMode().toLowerCase(),
                    onLoad: (aceInstance: Ace.Editor) => {
                        aceInstance.setShowPrintMargin(false);
                        aceInstance.commands.addCommand({
                            name: 'saving',
                            bindKey: {win: 'Ctrl-S', mac: 'Command-S'},
                            exec: () => {
                                this.saveContent();
                            },
                            readOnly: false
                        });

                        this.onEditorInit({$editor: aceInstance});
                    }
                };

                this.loadContent();
            }

            if (currentFile.viewForms.includes(FileViewForm.IMAGE)) {
                const img = new Image();
                const imageFile = (currentFile as ImageFile);
                img.src = this.CONTEXT_PATH + imageFile.urlPath;
                imageFile.width = img.naturalWidth;
                imageFile.height = img.naturalHeight;
            }

        }
    }

    public getMode(): EditorMode {
        if (!this.file.viewForms.includes(FileViewForm.PLAIN_TEXT)) {
            return undefined;
        }

        const filename = this.file.filename;

        if (filename) {
            if (filename.endsWith('.vm') || filename.endsWith('.vtl')) {
                return EditorMode.VELOCITY;
            }
            if (filename.endsWith('.html')) {
                return EditorMode.VELOCITY;
            }
            if (filename.endsWith('.properties')) {
                return EditorMode.PROPERTIES;
            }
            if (filename.endsWith('.css')) {
                return EditorMode.CSS;
            }
        }

        return DEFAULT_EDITOR_MODE;
    }

    public isWysiwygCapable(): boolean {
        const filename = this.file.filename;

        return filename && (
            filename.endsWith('.vm') ||
            filename.endsWith('.vtl') ||
            filename.endsWith('.htm') ||
            filename.endsWith('.html'));
    }

    @ngAsync()
    public async loadContent(): Promise<any> {
        this.state = 'PROCESSING';

        try {
            const contentResp = await this.fileService.getFileContent(this.file);

            this.savedContent = this.contentHolder.value = contentResp;
        } finally {
            this.state = 'NORMAL';
        }

        return this.contentHolder.value;
    }

    @ngAsync()
    public async saveContent(): Promise<ViewableFile> {
        this.state = 'PROCESSING';
        this.savedContent = this.contentHolder.value;

        let respFile;

        try {
            respFile = await this.fileService.saveFileContent(this.file, this.contentHolder.value);

            // soubor jako takovy nebudeme nahrazovat
            this.file.size = respFile.size;
        } finally {
            this.state = 'NORMAL';
        }

        return respFile;
    }

    public isUnsaved(): boolean {
        return this.contentHolder.value !== this.savedContent;
    }

    public renderHtml(html: string): string {
        return this.$sce.trustAsHtml(html);
    }
}

/**
 * @ngdoc component
 * @name kpFileEditor
 * @module portaro.components
 *
 * @param {File} file Editing file
 * @param {function()} [onEditorInit] Callback after initialization of Ace editor.
 * @param {AceAjax.Editor} [onEditorInit.$editor] Editor instance
 *
 * @description
 * File editor component.
 */
// eslint-disable-next-line max-classes-per-file
export default class KpFileEditorComponent implements Component<KpFileEditorComponentBindings> {
    public static componentName = 'kpFileEditor';

    public bindings = {
        file: '<',
        onEditorInit: '&'
    } as const;

    public controller = KpFileEditorComponentController;
    public templateUrl = require('./kp-file-editor.tpl.ftl');
}

interface KpFileEditorComponentBindings {
    file: ViewableFile;

    onEditorInit(locals: { $editor: Ace.Editor });
}
